"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from '@/lib/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, Save, Shield, Key, Server, Bell, Palette, Loader, Download, Upload, User, Lock, Eye, EyeOff, UserCog, AlertCircle } from "lucide-react";

const API_BASE =
  import.meta.env?.VITE_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:3001" : "");

export function SettingsPage() {
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("general");

  const { data: status } = useQuery({
    queryKey: ["status"],
    queryFn: () => api.getStatus(token!),
    enabled: !!token,
  });

  // General settings
  const [generalSettings, setGeneralSettings] = useState({
    dashboardTitle: "RustDesk Pro",
    sessionTimeout: 24,
    enableRegistration: false,
    maintenanceMode: false,
  });

  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    require2FA: false,
    passwordMinLength: 8,
    maxLoginAttempts: 5,
    lockoutDuration: 15,
    apiRateLimit: 100,
  });

  // Server settings
  const [serverSettings, setServerSettings] = useState({
    idServer: "your-domain.com",
    relayServer: "your-domain.com",
    apiPort: 21114,
    enableSSL: true,
    sslCertPath: "/etc/ssl/certs/rustdesk.pem",
    sslKeyPath: "/etc/ssl/private/rustdesk.key",
  });

  // Notifications
  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: false,
    emailSmtpHost: "",
    emailSmtpPort: 587,
    emailUsername: "",
    emailPassword: "",
    emailFrom: "",
    slackWebhook: "",
    discordWebhook: "",
    telegramBotToken: "",
    telegramChatId: "",
  });

  // Appearance
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: "system",
    primaryColor: "#2563eb",
    logoUrl: "",
    faviconUrl: "",
    customCss: "",
  });

  const saveGeneral = useMutation({
    mutationFn: async () => { /* API call would go here */ },
    onSuccess: () => { /* queryClient.invalidateQueries({ queryKey: ["settings"] }); */ },
  });

  const saveSecurity = useMutation({
    mutationFn: async () => { /* API call would go here */ },
  });

  const saveServer = useMutation({
    mutationFn: async () => { /* API call would go here */ },
  });

  const saveNotifications = useMutation({
    mutationFn: async () => { /* API call would go here */ },
  });

  const saveAppearance = useMutation({
    mutationFn: async () => { /* API call would go here */ },
  });

  const restartServices = useMutation({
    mutationFn: () => api.restartServices(token!),
  });

  const generateKey = useMutation({
    mutationFn: () => api.generateKey(token!),
  });

  const getLogs = useMutation({
    mutationFn: (lines: number) => api.getLogs(lines, token!),
  });

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const res = await fetch(`${API_BASE}/api/me/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to change password" }));
        throw new Error(err.error || "Failed to change password");
      }
      return res.json();
    },
    onSuccess: () => {
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordError("");
    },
    onError: (error: Error) => {
      setPasswordError(error.message);
    },
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    if (!passwordForm.currentPassword) {
      setPasswordError("Current password is required");
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const handlePasswordFormChange = (field: string, value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    setPasswordError("");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Configure your RustDesk Pro dashboard</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="general"><Palette className="mr-2 h-4 w-4" />General</TabsTrigger>
          <TabsTrigger value="profile"><UserCog className="mr-2 h-4 w-4" />Profile</TabsTrigger>
          <TabsTrigger value="security"><Shield className="mr-2 h-4 w-4" />Security</TabsTrigger>
          <TabsTrigger value="server"><Server className="mr-2 h-4 w-4" />Server</TabsTrigger>
          <TabsTrigger value="notifications"><Bell className="mr-2 h-4 w-4" />Notifications</TabsTrigger>
          <TabsTrigger value="appearance"><Palette className="mr-2 h-4 w-4" />Appearance</TabsTrigger>
          <TabsTrigger value="maintenance"><Loader className="mr-2 h-4 w-4" />Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>General Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label htmlFor="dashboardTitle">Dashboard Title</Label><Input id="dashboardTitle" value={generalSettings.dashboardTitle} onChange={(e) => setGeneralSettings({ ...generalSettings, dashboardTitle: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="sessionTimeout">Session Timeout (hours)</Label><Input id="sessionTimeout" type="number" value={generalSettings.sessionTimeout} onChange={(e) => setGeneralSettings({ ...generalSettings, sessionTimeout: parseInt(e.target.value) || 24 })} /></div>
              <div className="flex items-center space-x-2"><input type="checkbox" id="enableRegistration" checked={generalSettings.enableRegistration} onChange={(e) => setGeneralSettings({ ...generalSettings, enableRegistration: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /><Label htmlFor="enableRegistration">Enable user registration</Label></div>
              <div className="flex items-center space-x-2"><input type="checkbox" id="maintenanceMode" checked={generalSettings.maintenanceMode} onChange={(e) => setGeneralSettings({ ...generalSettings, maintenanceMode: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /><Label htmlFor="maintenanceMode">Maintenance mode (disable dashboard access)</Label></div>
              <Button onClick={() => saveGeneral.mutate()} disabled={saveGeneral.isPending}>{saveGeneral.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Changes</>}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile & Password</CardTitle>
              <CardDescription>Manage your profile and change your password</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border-b pb-6">
                <h3 className="text-lg font-medium mb-4">Profile Information</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="profileUsername">Username</Label>
                    <Input id="profileUsername" value={user?.username || ""} disabled className="bg-muted" />
                    <p className="text-xs text-muted-foreground">Username cannot be changed</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profileRole">Role</Label>
                    <Input id="profileRole" value={user?.role || ""} disabled className="bg-muted" />
                  </div>
                </div>
              </div>

              <div className="border-b pb-6">
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Change Password
                </h3>
                <p className="text-sm text-muted-foreground mb-4">Leave fields empty to keep current password</p>
                <div className="grid gap-4 sm:grid-cols-2 max-w-md">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        placeholder="Enter current password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-[38px] h-8 w-8"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        placeholder="Enter new password (min 8 characters)"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-[38px] h-8 w-8"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        aria-label={showNewPassword ? "Hide password" : "Show password"}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Min 8 characters</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-[38px] h-8 w-8"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                {passwordError && (
                  <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                    <AlertCircle className="h-4 w-4" />
                    {passwordError}
                  </div>
                )}
                <Button
                  onClick={handlePasswordChange}
                  disabled={changePasswordMutation.isPending}
                  className="w-full sm:w-auto"
                >
                  {changePasswordMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Update Password
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Authentication & Security</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2"><input type="checkbox" id="require2FA" checked={securitySettings.require2FA} onChange={(e) => setSecuritySettings({ ...securitySettings, require2FA: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /><Label htmlFor="require2FA">Require Two-Factor Authentication for admins</Label></div>
              <div className="space-y-2"><Label htmlFor="passwordMinLength">Minimum Password Length</Label><Input id="passwordMinLength" type="number" value={securitySettings.passwordMinLength} onChange={(e) => setSecuritySettings({ ...securitySettings, passwordMinLength: parseInt(e.target.value) || 8 })} /></div>
              <div className="space-y-2"><Label htmlFor="maxLoginAttempts">Max Login Attempts</Label><Input id="maxLoginAttempts" type="number" value={securitySettings.maxLoginAttempts} onChange={(e) => setSecuritySettings({ ...securitySettings, maxLoginAttempts: parseInt(e.target.value) || 5 })} /></div>
              <div className="space-y-2"><Label htmlFor="lockoutDuration">Lockout Duration (minutes)</Label><Input id="lockoutDuration" type="number" value={securitySettings.lockoutDuration} onChange={(e) => setSecuritySettings({ ...securitySettings, lockoutDuration: parseInt(e.target.value) || 15 })} /></div>
              <div className="space-y-2"><Label htmlFor="apiRateLimit">API Rate Limit (requests/minute)</Label><Input id="apiRateLimit" type="number" value={securitySettings.apiRateLimit} onChange={(e) => setSecuritySettings({ ...securitySettings, apiRateLimit: parseInt(e.target.value) || 100 })} /></div>
              <Button onClick={() => saveSecurity.mutate()} disabled={saveSecurity.isPending}>{saveSecurity.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Changes</>}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="server" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Server Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label htmlFor="idServer">ID Server</Label><Input id="idServer" value={serverSettings.idServer} onChange={(e) => setServerSettings({ ...serverSettings, idServer: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="relayServer">Relay Server</Label><Input id="relayServer" value={serverSettings.relayServer} onChange={(e) => setServerSettings({ ...serverSettings, relayServer: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="apiPort">API Port</Label><Input id="apiPort" type="number" value={serverSettings.apiPort} onChange={(e) => setServerSettings({ ...serverSettings, apiPort: parseInt(e.target.value) || 21114 })} /></div>
              <div className="flex items-center space-x-2"><input type="checkbox" id="enableSSL" checked={serverSettings.enableSSL} onChange={(e) => setServerSettings({ ...serverSettings, enableSSL: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /><Label htmlFor="enableSSL">Enable SSL/TLS</Label></div>
              <div className="space-y-2"><Label htmlFor="sslCertPath">SSL Certificate Path</Label><Input id="sslCertPath" value={serverSettings.sslCertPath} onChange={(e) => setServerSettings({ ...serverSettings, sslCertPath: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="sslKeyPath">SSL Key Path</Label><Input id="sslKeyPath" value={serverSettings.sslKeyPath} onChange={(e) => setServerSettings({ ...serverSettings, sslKeyPath: e.target.value })} /></div>
              <Button onClick={() => saveServer.mutate()} disabled={saveServer.isPending}>{saveServer.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Changes</>}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Notification Channels</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2"><input type="checkbox" id="emailEnabled" checked={notificationSettings.emailEnabled} onChange={(e) => setNotificationSettings({ ...notificationSettings, emailEnabled: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /><Label htmlFor="emailEnabled">Enable Email Notifications</Label></div>
              {notificationSettings.emailEnabled && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label htmlFor="emailSmtpHost">SMTP Host</Label><Input id="emailSmtpHost" value={notificationSettings.emailSmtpHost} onChange={(e) => setNotificationSettings({ ...notificationSettings, emailSmtpHost: e.target.value })} /></div>
                  <div className="space-y-2"><Label htmlFor="emailSmtpPort">SMTP Port</Label><Input id="emailSmtpPort" type="number" value={notificationSettings.emailSmtpPort} onChange={(e) => setNotificationSettings({ ...notificationSettings, emailSmtpPort: parseInt(e.target.value) || 587 })} /></div>
                  <div className="space-y-2"><Label htmlFor="emailUsername">Username</Label><Input id="emailUsername" value={notificationSettings.emailUsername} onChange={(e) => setNotificationSettings({ ...notificationSettings, emailUsername: e.target.value })} /></div>
                  <div className="space-y-2"><Label htmlFor="emailPassword">Password</Label><Input id="emailPassword" type="password" value={notificationSettings.emailPassword} onChange={(e) => setNotificationSettings({ ...notificationSettings, emailPassword: e.target.value })} /></div>
                  <div className="space-y-2"><Label htmlFor="emailFrom">From Address</Label><Input id="emailFrom" value={notificationSettings.emailFrom} onChange={(e) => setNotificationSettings({ ...notificationSettings, emailFrom: e.target.value })} /></div>
                </div>
              )}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium">Webhook Integrations</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label htmlFor="slackWebhook">Slack Webhook URL</Label><Input id="slackWebhook" value={notificationSettings.slackWebhook} onChange={(e) => setNotificationSettings({ ...notificationSettings, slackWebhook: e.target.value })} placeholder="https://hooks.slack.com/services/..." /></div>
                  <div className="space-y-2"><Label htmlFor="discordWebhook">Discord Webhook URL</Label><Input id="discordWebhook" value={notificationSettings.discordWebhook} onChange={(e) => setNotificationSettings({ ...notificationSettings, discordWebhook: e.target.value })} placeholder="https://discord.com/api/webhooks/..." /></div>
                  <div className="space-y-2"><Label htmlFor="telegramBotToken">Telegram Bot Token</Label><Input id="telegramBotToken" value={notificationSettings.telegramBotToken} onChange={(e) => setNotificationSettings({ ...notificationSettings, telegramBotToken: e.target.value })} /></div>
                  <div className="space-y-2"><Label htmlFor="telegramChatId">Telegram Chat ID</Label><Input id="telegramChatId" value={notificationSettings.telegramChatId} onChange={(e) => setNotificationSettings({ ...notificationSettings, telegramChatId: e.target.value })} /></div>
                </div>
              </div>
              <Button onClick={() => saveNotifications.mutate()} disabled={saveNotifications.isPending}>{saveNotifications.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Changes</>}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Appearance</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label htmlFor="theme">Theme</Label><select id="theme" value={appearanceSettings.theme} onChange={(e) => setAppearanceSettings({ ...appearanceSettings, theme: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"><option value="light">Light</option><option value="dark">Dark</option><option value="system">System</option></select></div>
              <div className="space-y-2"><Label htmlFor="primaryColor">Primary Color</Label><div className="flex items-center gap-2"><input type="color" id="primaryColor" value={appearanceSettings.primaryColor} onChange={(e) => setAppearanceSettings({ ...appearanceSettings, primaryColor: e.target.value })} className="h-10 w-10 rounded border" /><Input id="primaryColorText" value={appearanceSettings.primaryColor} onChange={(e) => setAppearanceSettings({ ...appearanceSettings, primaryColor: e.target.value })} className="w-40" /></div></div>
              <div className="space-y-2"><Label htmlFor="logoUrl">Logo URL</Label><Input id="logoUrl" value={appearanceSettings.logoUrl} onChange={(e) => setAppearanceSettings({ ...appearanceSettings, logoUrl: e.target.value })} placeholder="https://example.com/logo.png" /></div>
              <div className="space-y-2"><Label htmlFor="faviconUrl">Favicon URL</Label><Input id="faviconUrl" value={appearanceSettings.faviconUrl} onChange={(e) => setAppearanceSettings({ ...appearanceSettings, faviconUrl: e.target.value })} placeholder="https://example.com/favicon.ico" /></div>
              <div className="space-y-2"><Label htmlFor="customCss">Custom CSS</Label><textarea id="customCss" value={appearanceSettings.customCss} onChange={(e) => setAppearanceSettings({ ...appearanceSettings, customCss: e.target.value })} className="flex min-h-[150px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" placeholder="/* Custom CSS here */" /></div>
              <Button onClick={() => saveAppearance.mutate()} disabled={saveAppearance.isPending}>{saveAppearance.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Changes</>}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Service Management</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">RustDesk Services</h4>
                  <p className="text-sm text-muted-foreground">Manage hbbs (ID server) and hbbr (relay server) services</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => restartServices.mutate()} disabled={restartServices.isPending}>{restartServices.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Restarting...</> : "Restart Services"}</Button>
                  <Button variant="destructive" onClick={() => generateKey.mutate()} disabled={generateKey.isPending}>{generateKey.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : <><Key className="mr-2 h-4 w-4" />Rotate Key</>}</Button>
                </div>
              </div>
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-2">Current Status</h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="p-3 rounded-lg bg-muted">
                    <div className="text-sm text-muted-foreground">hbbs (ID Server)</div>
                    <div className={`font-medium ${status?.hbbs === "active" ? "text-green-600" : "text-red-600"}`}>{status?.hbbs || "Unknown"}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted">
                    <div className="text-sm text-muted-foreground">hbbr (Relay Server)</div>
                    <div className={`font-medium ${status?.hbbr === "active" ? "text-green-600" : "text-red-600"}`}>{status?.hbbr || "Unknown"}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Public Key Management</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-muted font-mono text-sm break-all">{status?.key || "Loading..."}</div>
              <Button onClick={() => generateKey.mutate()} disabled={generateKey.isPending}>{generateKey.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : <><Key className="mr-2 h-4 w-4" />Generate New Key</>}</Button>
              <p className="text-sm text-muted-foreground">Warning: Generating a new key will invalidate all existing client connections. Clients will need to be reconfigured with the new key.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>System Logs</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Button variant="outline" onClick={() => getLogs.mutate(100)} disabled={getLogs.isPending}>{getLogs.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading...</> : "View Last 100 Lines"}</Button>
                <Button variant="outline" onClick={() => getLogs.mutate(500)} disabled={getLogs.isPending}>View Last 500 Lines</Button>
                <Button variant="outline" onClick={() => getLogs.mutate(1000)} disabled={getLogs.isPending}>View Last 1000 Lines</Button>
              </div>
              {getLogs.isSuccess && (
                <div className="p-4 rounded-lg bg-muted font-mono text-sm max-h-[400px] overflow-auto whitespace-pre-wrap border">
                  {getLogs.data?.logs}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Backup & Restore</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Database Backup</h4>
                  <p className="text-sm text-muted-foreground">Download a backup of the dashboard database</p>
                </div>
                <Button variant="outline"><Download className="mr-2 h-4 w-4" />Download Backup</Button>
              </div>
              <div className="pt-4 border-t flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Restore Database</h4>
                  <p className="text-sm text-muted-foreground">Upload a backup file to restore the database</p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="file" accept=".db,.sqlite,.sqlite3" className="sr-only" id="restoreFile" />
                  <Label htmlFor="restoreFile" className="cursor-pointer"><Upload className="mr-2 h-4 w-4" />Choose File</Label>
                  <Button variant="outline">Restore</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}